import axios from 'axios';
import token from './token';
import history from './history';
import { SIGNIN_PATH } from '../constants/pathRedirect';

let isRefreshing = false;
let refreshSubscribers = [];

const matchType = {
  START_WITH: 'startWith',
  EXACT_MATCH: 'exactMatch',
};

const skipAuthEndpoints = [
  { prefix: '/users', method: 'post', match: matchType.EXACT_MATCH },
  { prefix: '/users/sendForgotPasswordEmail', method: 'get', match: matchType.START_WITH },
  { prefix: '/users/resetPassword', method: 'post', match: matchType.START_WITH },
  { prefix: '/shopify/auth', method: 'get', match: matchType.START_WITH },
];

const api = axios.create({
  baseURL: process.env.REACT_APP_API_HOST,
});

function shouldAddPathPrefix(config) {
  if (config.url.startsWith('/integrations/')) {
    return false;
  }

  return true;
}

function doesEndpointStartWith(configUrl, prefix, match) {
  return match === matchType.START_WITH && configUrl.startsWith(prefix);
}

function doesEndpointMatchUrl(configUrl, url, match) {
  return match === matchType.EXACT_MATCH && configUrl === url;
}

function shouldSkipAuth(config, prefix, method, match) {
  if (config.method !== method) {
    return false;
  }
  return doesEndpointStartWith(config.url, prefix, match) || doesEndpointMatchUrl(config.url, prefix, match);
}

function requiresAuth(config) {
  return !skipAuthEndpoints.some(({ prefix, method, match }) => {
    return shouldSkipAuth(config, prefix, method, match);
  });
}

async function refreshToken(localToken) {
  try {
    const params = new URLSearchParams();
    params.append('refresh_token', localToken.refresh_token);
    params.append('client_id', process.env.REACT_APP_CLIENT_ID);
    params.append('client_secret', process.env.REACT_APP_CLIENT_SECRET);
    params.append('grant_type', 'refresh_token');

    const response = await axios.post(process.env.REACT_APP_TOKEN_URL, params);
    return response.data;
  } catch (err) {
    return null;
  }
}

function afterRefreshed(token) {
  if (refreshSubscribers.length > 0) {
    refreshSubscribers.forEach((cb) => cb(token));
    refreshSubscribers = [];
  }
}

api.interceptors.request.use(
  async (config) => {
    const newConfig = config;
    const authRequired = requiresAuth(newConfig);

    // Add API path prefix to url if needed
    if (shouldAddPathPrefix(newConfig)) {
      newConfig.url = `${process.env.REACT_APP_API_PATH}${newConfig.url}`;
    }

    if (authRequired) {
      const localToken = token.getToken();
      if (!localToken) {
        history.push(SIGNIN_PATH);
        throw new axios.Cancel('Operation canceled: no auth');
      }

      if (token.needsRefresh(localToken)) {
        // If we are already refreshing the token, stash the request until end of refreshing
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            refreshSubscribers.push((accessToken) => {
              newConfig.headers.authorization = 'Bearer ' + accessToken;
              resolve(newConfig);
            });
          });
        } else {
          refreshSubscribers = [];
          isRefreshing = true;
          const newToken = await refreshToken(localToken);
          isRefreshing = false;

          if (newToken) {
            const userPermissions = [...token.getToken().user_permissions];
            token.setToken({ ...newToken, user_permissions: userPermissions });
            afterRefreshed(newToken.access_token);
            newConfig.headers.authorization = 'Bearer ' + newToken.access_token;
            return newConfig;
          } else {
            token.removeToken();
            history.push(SIGNIN_PATH);
            throw new axios.Cancel('Operation canceled: no auth');
          }
        }
      } else {
        newConfig.headers.authorization = 'Bearer ' + localToken.access_token;
        return newConfig;
      }
    } else {
      return newConfig;
    }
  },
  (error) => {
    return Promise.reject(error);
  },
);

api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response && error.response.status === 401) {
      token.removeToken();
      history.push(SIGNIN_PATH);
      throw new axios.Cancel('Operation canceled: no auth');
    }

    return Promise.reject(error);
  },
);

export const fetchWithRetry = async (
  apiFunction,
  returnedValueOnFinalError,
  delay = 0,
  maxRetries = 3,
  reportToMixpanel = () => {},
) => {
  let retryCount = 0;

  while (retryCount < maxRetries) {
    try {
      return await apiFunction();
    } catch (error) {
      console.error(`Attempt ${retryCount + 1} failed: ${error.message}`);
      reportToMixpanel({
        error: JSON.stringify(error),
      });

      retryCount++;

      if (retryCount < maxRetries) {
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
  }

  if (returnedValueOnFinalError) {
    return returnedValueOnFinalError;
  }

  throw new Error(`API call failed after ${maxRetries} attempts`);
};

api.CancelToken = axios.CancelToken;

export default api;

export function isResponseOk(response) {
  return response?.status === 200 || response?.status === 201;
}

export function emptySuccessfulResponse() {
  return Promise.resolve({ data: [], status: 200 });
}
